/**
 * API Client for @bosmart/api Integration
 *
 * Provides functions to interact with the monorepo API service
 * for interview data, analysis, and recordings.
 */

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3002'

function parseRetryAfterSeconds(header: string | null): number | undefined {
  if (!header) {
    return undefined
  }

  const parsed = Number.parseInt(header, 10)
  return Number.isFinite(parsed) ? parsed : undefined
}

export class ApiRequestError extends Error {
  status?: number
  retryAfterSeconds?: number
  body?: unknown

  constructor(
    message: string,
    options?: { status?: number; retryAfterSeconds?: number; body?: unknown }
  ) {
    super(message)
    this.name = 'ApiRequestError'
    this.status = options?.status
    this.retryAfterSeconds = options?.retryAfterSeconds
    this.body = options?.body
  }
}

export interface InterviewQuestion {
  id: string
  order: number
  title: string
  content: string
  category?: string | null
  difficulty?: string | null
  duration?: number | null
}

export interface ApiError {
  error: string
  message: string
  status?: number
}

export interface InterviewAccessDetails {
  interviewId: string
  publicId: string
  candidateName: string
  jobTitle: string | null
  duration: number | null
  interviewMode: 'ai' | 'question_set'
  requiresScreenSharing: boolean
}

export async function verifyInterviewOtp(
  interviewId: string,
  token: string,
  otp: string
): Promise<string> {
  const response = await fetch(`${API_BASE_URL}/api/v1/jobs/interviews/${interviewId}/verify-otp`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ token, otp }),
  })

  const data = await response.json().catch(() => null)

  if (!response.ok || !data?.success || typeof data?.proofToken !== 'string') {
    const errorMessage = data?.error || data?.message || 'Failed to verify interview code'
    const retryAfterHeader = response.headers.get('Retry-After')
    throw new ApiRequestError(errorMessage, {
      status: response.status,
      retryAfterSeconds: parseRetryAfterSeconds(retryAfterHeader),
    })
  }

  return data.proofToken
}

export async function resendInterviewOtp(
  interviewId: string,
  token: string
): Promise<{ message: string; expiresAt?: string | null }> {
  const response = await fetch(`${API_BASE_URL}/api/v1/jobs/interviews/${interviewId}/resend-otp`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ token }),
  })

  const data = await response.json().catch(() => null)

  if (!response.ok || !data?.success) {
    const errorMessage = data?.error || data?.message || 'Failed to resend interview code'
    const retryAfterHeader = response.headers.get('Retry-After')
    throw new ApiRequestError(errorMessage, {
      status: response.status,
      retryAfterSeconds: parseRetryAfterSeconds(retryAfterHeader),
    })
  }

  return {
    message: data?.message || 'A new verification code has been sent.',
    expiresAt: data?.expiresAt ?? null,
  }
}

export async function fetchInterviewAccessDetails(
  interviewId: string,
  token: string,
  proofToken: string
): Promise<InterviewAccessDetails> {
  const query = new URLSearchParams({ token, proof: proofToken })
  const response = await fetch(
    `${API_BASE_URL}/api/v1/jobs/interviews/${interviewId}/access-details?${query.toString()}`,
    {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    }
  )

  const data = await response.json().catch(() => null)

  if (!response.ok || !data?.success || !data?.data) {
    const errorMessage = data?.error || data?.message || 'Failed to load interview details'
    throw new ApiRequestError(errorMessage, {
      status: response.status,
      body: data,
    })
  }

  const details = data.data
  const isValidDetails =
    typeof details?.interviewId === 'string' &&
    typeof details?.publicId === 'string' &&
    typeof details?.candidateName === 'string' &&
    (details?.jobTitle === null || typeof details?.jobTitle === 'string') &&
    (details?.duration === null || typeof details?.duration === 'number') &&
    (details?.interviewMode === 'ai' || details?.interviewMode === 'question_set') &&
    typeof details?.requiresScreenSharing === 'boolean'

  if (!isValidDetails) {
    throw new ApiRequestError('Interview details response was malformed', {
      status: response.status,
      body: data,
    })
  }

  return details
}

export async function fetchInterviewLiveToken(
  interviewId: string,
  token?: string,
  proofToken?: string
): Promise<string> {
  const response = await fetch(`${API_BASE_URL}/api/v1/jobs/interviews/${interviewId}/live-token`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      ...(token ? { token } : {}),
      ...(proofToken ? { proofToken } : {}),
    }),
  })

  const data = await response.json().catch(() => null)

  if (!response.ok || !data?.success || typeof data?.token !== 'string') {
    const errorMessage = data?.error || data?.message || 'Failed to create live session token'
    throw new ApiRequestError(errorMessage, {
      status: response.status,
      body: data,
    })
  }

  return data.token
}

/**
 * Fetch interview performance analysis from API
 *
 * @param interviewId - The interview ID
 * @returns Analysis data or null if not found
 */
export async function fetchInterviewAnalysis(interviewId: string) {
  const response = await fetch(`${API_BASE_URL}/api/v1/interviews/${interviewId}/analysis`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  })

  if (response.status === 404) {
    return null // Analysis not found
  }

  if (!response.ok) {
    let errorMessage = `API error: ${response.statusText}`
    try {
      const error: ApiError = await response.json()
      errorMessage = error.message || errorMessage
    } catch {
      void 0
    }
    throw new Error(errorMessage)
  }

  return await response.json()
}

/**
 * Generate interview analysis via API
 *
 * @param interviewId - The interview ID
 * @returns Analysis data
 */
export async function generateInterviewAnalysis(interviewId: string) {
  const response = await fetch(`${API_BASE_URL}/api/v1/interviews/${interviewId}/analysis`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
  })

  if (!response.ok) {
    let errorMessage = `API error: ${response.statusText}`
    try {
      const error: ApiError = await response.json()
      errorMessage = error.message || errorMessage
    } catch {
      void 0
    }
    throw new Error(errorMessage)
  }

  return await response.json()
}

/**
 * Fetch interview questions for an interview
 *
 * @param interviewId - The interview ID
 * @param token - Optional interview access token
 * @returns Array of interview questions or empty array if not found
 */
export async function fetchInterviewQuestions(
  interviewId: string,
  token?: string,
  proofToken?: string
): Promise<InterviewQuestion[]> {
  try {
    const params = new URLSearchParams()
    if (token) params.set('token', token)
    if (proofToken) params.set('proof', proofToken)
    const url = params.size
      ? `${API_BASE_URL}/api/v1/jobs/interviews/${interviewId}/questions?${params.toString()}`
      : `${API_BASE_URL}/api/v1/jobs/interviews/${interviewId}/questions`

    const response = await fetch(url, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    })

    if (response.status === 404) {
      return [] // Questions not found
    }

    if (!response.ok) {
      let errorMessage = `API error: ${response.statusText}`
      try {
        const error: ApiError = await response.json()
        errorMessage = error.message || errorMessage
      } catch {
        void 0
      }
      throw new Error(errorMessage)
    }

    const data = await response.json()

    // Handle both wrapped and direct response formats
    let questions: InterviewQuestion[] = []
    if (data.success && data.data && Array.isArray(data.data.questions)) {
      questions = data.data.questions
    } else if (Array.isArray(data.questions)) {
      // Direct format (as returned by the API)
      questions = data.questions
    } else if (data.data && Array.isArray(data.data)) {
      questions = data.data
    }

    // Map to expected format
    // Prioritize API fields: order, title, content (now properly returned by API)
    // Keep fallbacks for backward compatibility with older API versions
    return questions.map((q: any) => ({
      id: q.id,
      order: q.order ?? 0, // Now properly returned by API
      title: q.title || q.question || q.content || '', // Prioritize title (brief summary)
      content: q.content || q.question || q.title || '', // Prioritize content (full text)
      category: q.category || q.type || null,
      difficulty: q.difficulty || null,
      duration: q.duration || null,
    }))
  } catch (error) {
    return [] // Return empty array on error to allow interview to continue
  }
}

/**
 * Get presigned URL for interview recording
 *
 * @param interviewId - The interview ID
 * @returns Presigned URL for the recording
 */
export async function getRecordingSignedUrl(interviewId: string): Promise<string> {
  const response = await fetch(
    `${API_BASE_URL}/api/v1/interviews/${interviewId}/recording/signed-url`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    }
  )

  if (!response.ok) {
    const error: ApiError = await response.json()
    throw new Error(error.message || `API error: ${response.statusText}`)
  }

  const data = await response.json()
  if (!data.url || typeof data.url !== 'string') {
    throw new Error('Invalid response: missing or invalid URL')
  }
  return data.url
}

/**
 * Search interview transcript
 *
 * @param interviewId - The interview ID
 * @param query - Search query
 * @returns Search results
 */
export async function searchTranscript(interviewId: string, query: string) {
  const response = await fetch(
    `${API_BASE_URL}/api/v1/interviews/${interviewId}/transcript/search?q=${encodeURIComponent(query)}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    }
  )

  if (!response.ok) {
    let errorMessage = `API error: ${response.statusText}`
    try {
      const error: ApiError = await response.json()
      errorMessage = error.message || errorMessage
    } catch {
      void 0
    }
    throw new Error(errorMessage)
  }

  return await response.json()
}

/**
 * Check if API is available
 */
export async function checkApiHealth(): Promise<boolean> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/health`, {
      method: 'GET',
    })
    return response.ok
  } catch {
    return false
  }
}

/**
 * Save recording metadata to API
 *
 * @param interviewId - The interview ID
 * @param recordingUrl - URL of the uploaded recording
 * @param civoKey - CIVO storage key
 * @param duration - Duration in seconds
 * @param size - File size in bytes
 * @param mimeType - MIME type of the recording
 * @param token - Optional interview access token
 */
export async function saveRecording(
  interviewId: string,
  recordingUrl: string,
  civoKey?: string,
  duration?: number,
  size?: number,
  mimeType?: string,
  token?: string
) {
  const url = token
    ? `${API_BASE_URL}/api/v1/jobs/interviews/${interviewId}/recording/save?token=${encodeURIComponent(token)}`
    : `${API_BASE_URL}/api/v1/jobs/interviews/${interviewId}/recording/save`

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({
      recordingUrl,
      civoKey,
      duration,
      size,
      mimeType,
    }),
  })

  if (!response.ok) {
    let errorMessage = `API error: ${response.statusText}`
    try {
      const error: ApiError = await response.json()
      errorMessage = error.message || errorMessage
    } catch {
      void 0
    }
    throw new Error(errorMessage)
  }

  return await response.json()
}

/**
 * Save transcription segments to API
 *
 * @param interviewId - The interview ID
 * @param segments - Array of transcript segments
 * @param token - Optional interview access token
 */
export async function saveTranscription(
  interviewId: string,
  segments: Array<{
    speakerId: string
    speakerName?: string | null
    text: string
    startTime: string // ISO timestamp
    stopTime: string // ISO timestamp
    sequenceNumber: number
  }>,
  token?: string
) {
  const url = token
    ? `${API_BASE_URL}/api/v1/jobs/interviews/${interviewId}/transcription/save?token=${encodeURIComponent(token)}`
    : `${API_BASE_URL}/api/v1/jobs/interviews/${interviewId}/transcription/save`

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({
      segments,
    }),
  })

  if (!response.ok) {
    let errorMessage = `API error: ${response.statusText}`
    try {
      const error: ApiError = await response.json()
      errorMessage = error.message || errorMessage
    } catch {
      void 0
    }
    throw new Error(errorMessage)
  }

  return await response.json()
}

/**
 * Trigger interview background processing
 * Queues a job in the database for the video processing worker to handle
 * @param interviewId - Interview ID
 * @param data - Processing data (uploadMetadata, recordingMetadata, transcription, etc.)
 */
export async function triggerInterviewProcessing(
  interviewId: string,
  data: {
    uploadMetadata?: any
    recordingMetadata?: any
    transcription?: any
    duration?: number
    token?: string
  }
): Promise<void> {
  const url = `${API_BASE_URL}/api/v1/jobs/interviews/${interviewId}/process`
  const maxRetries = 3
  const retryDelay = 1000 // 1 second

  // Only log in development to avoid exposing sensitive info (interviewId) in production
  if (import.meta.env.DEV) {
    void 0
  }

  let lastError: Error | null = null

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      if (import.meta.env.DEV) {
        void 0
      }

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
        signal: AbortSignal.timeout(30000), // 30 second timeout
      })

      if (import.meta.env.DEV) {
        void 0
      }

      if (!response.ok) {
        let errorData: any
        try {
          errorData = await response.json()
        } catch {
          // If JSON parsing fails, create error from status
          errorData = { error: response.statusText || `HTTP ${response.status}` }
        }

        // Extract error message from multiple possible fields
        const errorMessage =
          errorData.error ||
          errorData.message ||
          errorData.details ||
          `Failed to trigger interview processing: ${response.status} ${response.statusText}`

        const error = new Error(errorMessage)

        // Don't retry on 4xx errors (client errors - bad request, validation errors, etc.)
        if (response.status >= 400 && response.status < 500) {
          throw error
        }

        // Retry on 5xx errors (server errors) - these are transient
        lastError = error
        if (attempt < maxRetries) {
          const delay = retryDelay * attempt
          await new Promise((resolve) => setTimeout(resolve, delay))
          continue
        }
        throw error
      }

      const responseData = await response.json()

      // CRITICAL: Verify the response explicitly indicates success
      // Check for explicit true value to handle false, undefined, null, or missing success field
      if (responseData.success !== true) {
        // Extract error message from multiple possible fields
        const errorMessage =
          responseData.error ||
          responseData.message ||
          responseData.details ||
          'Interview processing failed - response did not indicate success'

        const error = new Error(errorMessage)

        // Determine if this is retryable (5xx errors) or not (4xx errors)
        // If we got here with response.ok === true but success !== true, it's likely a business logic error
        // Don't retry on business logic errors
        throw error
      }

      // Additional validation: verify critical fields exist for successful response
      // Handle both response formats:
      // 1. Direct format: { success: true, interviewId, jobId, ... }
      // 2. Wrapped format: { success: true, data: { interviewId, jobId, ... }, ... }
      const actualInterviewId = responseData.interviewId || responseData.data?.interviewId
      const actualJobId = responseData.jobId || responseData.data?.jobId

      if (!actualInterviewId) {
        void 0
      }

      // Validate jobId exists - critical for tracking job status
      // If jobId is missing, the job may not have been properly queued
      if (!actualJobId) {
        void 0
      }

      // Only log in development to avoid exposing sensitive info
      if (import.meta.env.DEV) {
        void 0
      }

      return // Success - exit retry loop
    } catch (error) {
      const isNetworkError =
        error instanceof Error &&
        (error.message.includes('fetch failed') ||
          error.message.includes('network') ||
          error.message.includes('timeout') ||
          error.name === 'AbortError' ||
          error.name === 'TypeError')

      lastError = error instanceof Error ? error : new Error(String(error))

      // Retry on network errors, but not on client errors (4xx)
      if (attempt < maxRetries && isNetworkError) {
        const delay = retryDelay * attempt
        await new Promise((resolve) => setTimeout(resolve, delay))
        continue
      }

      // Last attempt failed or non-retryable error
      throw lastError
    }
  }

  // Should never reach here, but TypeScript requires it
  if (lastError) {
    throw lastError
  }
  throw new Error('Failed to trigger interview processing after all retries')
}

/**
 * @deprecated Use triggerInterviewProcessing instead - follows consistent pattern via ai-service
 * Trigger Inngest event for background processing
 *
 * @param eventName - The Inngest event name
 * @param data - Event data payload
 */
export async function triggerInngestEvent(eventName: string, data: any): Promise<void> {
  // Inngest events are triggered via the API endpoint
  // The API will handle forwarding to Inngest
  const response = await fetch(`${API_BASE_URL}/api/inngest/trigger`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      name: eventName,
      data,
    }),
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: response.statusText }))
    throw new Error(errorData.error || `Failed to trigger Inngest event: ${response.statusText}`)
  }
}

/**
 * Update interview status to completed
 *
 * @param interviewId - The interview ID
 * @param duration - Interview duration in seconds
 * @param token - Optional authentication token
 */
export async function updateInterviewStatus(
  interviewId: string,
  duration: number,
  token?: string | null
): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/api/v1/jobs/interviews/${interviewId}/status`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({
      status: 'completed',
      duration: Math.round(duration),
      completedAt: new Date().toISOString(),
      endedAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    }),
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: response.statusText }))
    throw new Error(errorData.error || `Failed to update interview status: ${response.statusText}`)
  }
}
